/**
 * "Why?" on a step of the record view (AI-K, `POST /bp/recipe-ai/knowledge/why/`). Opens a popover with the AI draft
 * of why the step exists, reconstructed by the server from the record's versions, change controls, the procedures the
 * step cites and deviations raised at it — only the sources the person may read. When nothing documents a reason the
 * server says so; the page never adds one. Read-only: nothing is saved. Hidden after a 503 (AI off) or 403.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import { Button, Popover, theme } from 'antd';
import { Info } from 'lucide-react';
import AiDraftCard from './AiDraftCard';
import './StepWhy.css';

const URL = '/bp/recipe-ai/knowledge/why/';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('Token')}` } });
const SOURCE_NAMES = { change_controls: 'change controls', sops: 'procedures', deviations: 'deviations' };

function History({ history }) {
  const { token } = theme.useToken();
  const sub = { fontSize: 12, color: token.colorTextSecondary, margin: '4px 0 0' };
  const versions = (history?.versions || []).filter((v) => v.introduced || v.step_changed);
  const lines = [
    ...versions.map((v) => `${v.introduced ? 'Introduced in' : 'Reworded in'} ${v.key}${v.change_control ? ` · ${v.change_control}` : ''}${v.note ? ` · ${v.note}` : ''}`),
    ...(history?.change_controls || []).map((c) => `${c.key} · ${c.title} · ${c.status}`),
    ...(history?.sops || []).flatMap((s) => (s.revisions || []).filter((r) => r.summary_of_change).map((r) => `${r.key} · ${r.summary_of_change}`)),
    ...(history?.deviations || []).map((d) => `${d.key} · ${d.title || d.kind}${d.disposition_reason ? ` · ${d.disposition_reason}` : ''}`),
  ];
  const notChecked = (history?.not_checked || []).map((k) => SOURCE_NAMES[k] || k);
  return (
    <>
      {lines.length ? (
        <ul style={{ ...sub, paddingInlineStart: 16 }} aria-label="Documented history" data-testid="step-why-history">
          {lines.map((l) => <li key={l}>{l}</li>)}
        </ul>
      ) : null}
      {notChecked.length ? <p style={sub} data-testid="step-why-not-checked">Not checked (no access): {notChecked.join(', ')}</p> : null}
    </>
  );
}

History.propTypes = { history: PropTypes.shape({}) };
History.defaultProps = { history: null };

export default function StepWhy({ recipeId, code }) {
  const [state, setState] = useState({ loading: false, data: null, error: '', off: false });
  if (state.off || !recipeId || !code) return null;
  const run = () => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    Axios.post(URL, { recipe_id: recipeId, code }, auth())
      .then((r) => setState({ loading: false, data: r.data?.data, error: '', off: false }))
      .catch((e) => setState({ loading: false, data: null, off: [403, 503].includes(e.response?.status),
        error: e.response?.data?.message || 'Could not be loaded.' }));
  };
  const d = state.data;
  const content = (
    <div style={{ width: 380, maxWidth: '80vw' }} data-testid={`step-why-${code}`}>
      {state.error ? <p style={{ margin: 0 }}>{state.error}</p> : (
        <AiDraftCard title="Why this step exists" text={d?.answer} loading={state.loading} sources={d?.sources}
          onRefresh={d ? run : undefined} testId="step-why-card">
          {d ? <History history={d.history} /> : null}
        </AiDraftCard>
      )}
    </div>
  );
  return (
    <Popover trigger="click" placement="rightTop" content={content}
      onOpenChange={(open) => { if (open && !d && !state.loading) run(); }}>
      <Button type="text" size="small" className="ai-step-why" data-html2canvas-ignore="true"
        icon={<Info size={13} aria-hidden />} aria-label={`Why does step ${code} exist?`} data-testid={`step-why-open-${code}`}>
        Why?
      </Button>
    </Popover>
  );
}

StepWhy.propTypes = {
  recipeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  code: PropTypes.string,
};

StepWhy.defaultProps = { recipeId: null, code: '' };
